// frontend/packages/business-console/src/components/help/WelcomeChecklist.jsx
//
// First-login welcome panel. Shows what clients CAN do in the CMS
// and what they should contact UpRyzze for. Dismissible, persisted
// in localStorage so it only shows once.

import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

const STORAGE_KEY = "upryzze_welcome_dismissed";

const CAN_DO = [
  "Update text, headlines, and descriptions on your pages",
  "Add new blog posts, team members, and testimonials",
  "Upload and swap photos and images",
  "Update your contact info, phone number, and hours",
  "Add or edit FAQs",
  "Preview changes before publishing",
];

const CONTACT_FOR = [
  "Adding new pages or sections to your site",
  "Changing your site layout or navigation structure",
  "Design changes (fonts, colors, spacing)",
  "Technical settings (analytics, integrations, forms)",
  "Domain or hosting changes",
];

export default function WelcomeChecklist() {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      const val = localStorage.getItem(STORAGE_KEY);
      setDismissed(val === "true");
    } catch (e) {
      /* localStorage may be unavailable */
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch (e) {
      /* ignore */
    }
  };

  if (dismissed) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 2,
        position: "relative",
        background: "linear-gradient(135deg, rgba(52,152,219,0.05) 0%, rgba(52,152,219,0.02) 100%)",
      }}
    >
      <IconButton
        onClick={handleDismiss}
        sx={{ position: "absolute", top: 8, right: 8 }}
        size="small"
        aria-label="Dismiss welcome guide"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
        Welcome to your website dashboard
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
        Here you can make simple updates to your website content. For bigger changes, our team is here to help.
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: "success.main" }}>
        Things you can do here
      </Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {CAN_DO.map((item, i) => (
          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "success.main" }} />
            </ListItemIcon>
            <ListItemText
              primary={item}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, mt: 1.5 }}>
        Contact the UpRyzze team for
      </Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {CONTACT_FOR.map((item, i) => (
          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SupportAgentIcon sx={{ fontSize: 18, opacity: 0.5 }} />
            </ListItemIcon>
            <ListItemText
              primary={item}
              primaryTypographyProps={{ variant: "body2", sx: { opacity: 0.8 } }}
            />
          </ListItem>
        ))}
      </List>

      <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
        <strong>Tip:</strong> We recommend making simple text and image changes only.
        For anything structural, reach out to us and we will take care of it.
      </Alert>

      <Box sx={{ mt: 2, textAlign: "right" }}>
        <Button variant="outlined" size="small" onClick={handleDismiss}>
          Got it, thanks
        </Button>
      </Box>
    </Paper>
  );
}
